import type { CheckInBeat } from '@/types/lesson';

/**
 * Evaluate a learner's free-form check-in answer against the beat's rubric.
 * Pure keyword heuristic — same contract as the real CodeBaby flow will
 * implement on its side (CB spec §2 Mode C). Swappable for an LLM grader
 * later without UI changes.
 *
 * `strong` verdict: at least one strong keyword matches AND no weak keyword
 * matches. Everything else is `weak`.
 */

export type CheckInVerdict = 'strong' | 'weak';

export interface CheckInEvaluation {
  verdict: CheckInVerdict;
  coach_message: string;
}

export function evaluateCheckIn(
  text: string,
  beat: CheckInBeat,
  attemptNumber: number,
): CheckInEvaluation {
  const lowered = text.toLowerCase();
  const hasStrong = beat.rubric.strong.some((k) => lowered.includes(k.toLowerCase()));
  const hasWeak = beat.rubric.weak.some((k) => lowered.includes(k.toLowerCase()));
  const verdict: CheckInVerdict = hasStrong && !hasWeak ? 'strong' : 'weak';

  if (verdict === 'strong') {
    return {
      verdict,
      coach_message:
        "That's the move. You didn't fight her framing — you turned her phrase back into a question. That's how skeptics start opening up.",
    };
  }

  // Weak, second attempt — brief says "unlock continue regardless."
  if (attemptNumber >= 2) {
    return {
      verdict,
      coach_message:
        "Good — let's keep moving, and we'll come back to this one in the roleplay. Exposure matters more than getting it right on the first pass.",
    };
  }

  // Weak, first attempt — redirect and let them try again.
  return {
    verdict,
    coach_message:
      "I see why you'd say that. But a real prospect hears a pitch before they hear 'I heard you.' Try again — acknowledge their framing first, then ask what it's actually paying for right now.",
  };
}
